import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatExerciseDisplay } from '../../../utils/exerciseFormatters';
import { formatRecoveryDisplaySeconds } from '../../../utils/formatRecoveryTime';
import { formatWorkoutRepsTarget } from '../../../constants/workoutRepsMode';
import { isPyramidExercise, formatSeriesPlanSummary, ensureSeriesPlan, formatWorkoutWeightTarget } from '../../../constants/workoutSeriesPlan';

/** Configurazione visiva set_type nel PDF (allineata a workoutSetTypes.js) */
const WORKOUT_SET_TYPE_PDF_CONFIG = {
    superset: {
        border: [147, 51, 234],
        bg: [250, 245, 255],
        label: 'SUPERSET',
    },
    circuit: {
        border: [239, 68, 68],
        bg: [254, 242, 242],
        label: 'CIRCUIT',
    },
    dropset: {
        border: [249, 115, 22],
        bg: [255, 247, 237],
        label: 'DROPSET',
    },
    giant_set: {
        border: [168, 85, 247],
        bg: [250, 245, 255],
        label: 'GIANT SET',
    },
    piramidale: {
        border: [59, 130, 246],
        bg: [239, 246, 255],
        label: 'PIRAMIDALE',
    },
};

function getWorkoutSetTypePdfConfig(setType) {
    const key = (setType || 'normal').toLowerCase();
    return WORKOUT_SET_TYPE_PDF_CONFIG[key] || null;
}

function drawSetTypePdfBadge(doc, setType, x, y) {
    const config = getWorkoutSetTypePdfConfig(setType);
    if (!config) return y;

    doc.setFontSize(6);
    doc.setFont('helvetica', 'bold');
    doc.setFillColor(...config.bg);
    doc.setDrawColor(...config.border);
    doc.setTextColor(...config.border);
    const labelW = doc.getTextWidth(config.label) + 4;
    doc.roundedRect(x, y, labelW, 3.8, 1, 1, 'FD');
    doc.text(config.label, x + 2, y + 2.7);
    return y + 4.5;
}

/**
 * Genera PDF scheda allenamento professionale
 */
export const generateWorkoutPDF = async (workout, athlete, gymLogoUrl, gymName = 'FitGym', apiBaseUrl = null) => {
    // Costruisci base URL API se non fornito
    if (!apiBaseUrl) {
        apiBaseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin + '/api';
    }
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
    });
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 12;
    const contentWidth = pageWidth - (2 * margin);
    
    let yPosition = margin;

    // ========== HEADER COMPATTO ==========
    
    // Logo (se presente) - più piccolo, mantiene proporzioni
    if (gymLogoUrl) {
        try {
            const logoImg = await loadLogoWithDimensions(gymLogoUrl);
            if (logoImg && logoImg.width && logoImg.height) {
                // Calcola dimensioni proporzionali mantenendo aspect ratio
                const maxWidth = 20; // mm
                const maxHeight = 20; // mm
                const aspectRatio = logoImg.width / logoImg.height;
                
                let logoWidth, logoHeight;
                if (aspectRatio > 1) {
                    // Logo orizzontale (larghezza > altezza)
                    logoWidth = Math.min(maxWidth, maxHeight * aspectRatio);
                    logoHeight = logoWidth / aspectRatio;
                } else {
                    // Logo verticale o quadrato (altezza >= larghezza)
                    logoHeight = Math.min(maxHeight, maxWidth / aspectRatio);
                    logoWidth = logoHeight * aspectRatio;
                }
                
                doc.addImage(logoImg.dataUrl, logoImg.format || 'PNG', margin, yPosition, logoWidth, logoHeight);
            }
        } catch (err) {
            console.warn('Logo non caricato:', err);
        }
    }
    
    // Titolo più compatto
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text('SCHEDA ALLENAMENTO', pageWidth / 2, yPosition + 8, { align: 'center' });
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(gymName || 'FitGymTrack', pageWidth / 2, yPosition + 13, { align: 'center' });
    
    yPosition += 23;
    
    // Linea separatore più sottile
    doc.setDrawColor(37, 99, 235);
    doc.setLineWidth(0.3);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 3;
    
    // ========== INFO ATLETA COMPATTA (4 COLONNE CON OBIETTIVO) ==========
    
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, yPosition, contentWidth, 12, 2, 2, 'F');
    
    const infoY = yPosition + 4;
    const colWidth = contentWidth / 4; // 4 colonne: Atleta, Data, Scheda, Obiettivo
    
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    
    // Atleta
    doc.text('Atleta:', margin + 3, infoY);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(athlete.name || 'N/A', margin + 3, infoY + 4);
    
    // Data
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text('Data:', margin + colWidth + 3, infoY);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    const today = new Date().toLocaleDateString('it-IT');
    doc.text(today, margin + colWidth + 3, infoY + 4);
    
    // Scheda
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text('Scheda:', margin + colWidth * 2 + 3, infoY);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    const schedaName = doc.splitTextToSize(workout.nome || 'N/A', colWidth - 8);
    doc.text(schedaName[0], margin + colWidth * 2 + 3, infoY + 4);
    
    // Obiettivo (se presente)
    if (workout.descrizione) {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text('Obiettivo:', margin + colWidth * 3 + 3, infoY);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 58, 138);
        const obiettivo = doc.splitTextToSize(workout.descrizione, colWidth - 8);
        doc.text(obiettivo[0], margin + colWidth * 3 + 3, infoY + 4);
    }
    
    yPosition += 12; // Bilanciato: 12mm - spazio header → esercizi
    
    // ========== ESERCIZI ==========
    
    const esercizi = workout.esercizi || [];
    
    for (let i = 0; i < esercizi.length; i++) {
        const esercizio = esercizi[i];
        
        // Salta se è collegato al precedente (verrà gestito come gruppo)
        if (esercizio.linked_to_previous == 1) {
            continue;
        }
        
        // Raccogli tutti gli esercizi collegati (gruppo)
        const gruppo = [esercizio];
        let j = i + 1;
        while (j < esercizi.length && esercizi[j].linked_to_previous == 1) {
            gruppo.push(esercizi[j]);
            j++;
        }
        
        // Check se serve nuova pagina
        if (yPosition > pageHeight - 60) {
            doc.addPage();
            yPosition = margin;
        }
        
        // GRUPPO DI ESERCIZI (Superset/Circuit/Dropset/Giant Set/etc)
        if (gruppo.length > 1) {
            const setType = (esercizio.set_type || 'superset').toLowerCase();
            const config = getWorkoutSetTypePdfConfig(setType) || {
                border: [71, 85, 105],        // gray-600
                bg: [248, 250, 252],          // gray-50
                label: setType.toUpperCase()
            };
            
            const borderColor = config.border;
            const bgColor = config.bg;
            const labelText = config.label;
            
            // Salva posizione iniziale per la cornice
            const groupStartY = yPosition;
            
            // STEP 1: Disegna sfondo colorato iniziale con altezza stimata
            // (verrà "tagliato" alla fine se l'altezza effettiva è minore)
            const estimatedHeight = gruppo.length * 35; // Stima iniziale (leggermente sovrastimata)
            doc.setFillColor(...bgColor);
            doc.roundedRect(margin, groupStartY, contentWidth, estimatedHeight, 3, 3, 'F');
            
            yPosition += 3; // Padding interno top
            
            // Label gruppo piccolo in alto a sinistra
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...borderColor);
            doc.text(labelText, margin + 4, yPosition + 2.5);
            yPosition += 5;
            
            // STEP 2: Rendering esercizi in 2 colonne (sopra lo sfondo)
            const colWidth = (contentWidth - 4) / 2; // 2mm gap
            const numCols = 2;
            const numRows = Math.ceil(gruppo.length / numCols);
            
            let currentY = yPosition;
            
            for (let row = 0; row < numRows; row++) {
                let rowMaxY = currentY;
                
                for (let col = 0; col < numCols; col++) {
                    const idx = row * numCols + col;
                    if (idx >= gruppo.length) break;
                    
                    const ex = gruppo[idx];
                    const xPos = margin + (col * (colWidth + 4));
                    // Usa await per caricare immagini asincrone
                    const finalY = await renderSingleExercise(doc, ex, i + idx + 1, xPos, currentY, colWidth, margin, apiBaseUrl);
                    rowMaxY = Math.max(rowMaxY, finalY);
                }
                
                currentY = rowMaxY + (row < numRows - 1 ? 1 : 0);
            }
            
            yPosition = currentY + 3; // Padding interno bottom
            
            // STEP 3: Calcola altezza corretta
            const groupHeight = yPosition - groupStartY;
            
            // STEP 4: Se l'altezza effettiva è minore di quella stimata, 
            // "taglia" la parte in eccesso dello sfondo disegnando un rettangolo bianco sopra
            if (groupHeight < estimatedHeight) {
                const excessHeight = estimatedHeight - groupHeight;
                const excessStartY = groupStartY + groupHeight;
                doc.setFillColor(255, 255, 255); // Bianco (colore pagina)
                doc.roundedRect(margin, excessStartY, contentWidth, excessHeight, 0, 0, 'F');
            }
            
            // STEP 5: Disegna bordo con altezza corretta sopra tutto
            doc.setDrawColor(...borderColor);
            doc.setLineWidth(0.8);
            doc.roundedRect(margin, groupStartY, contentWidth, groupHeight, 3, 3, 'S');
            
            yPosition += 3; // Spazio dopo il gruppo
            
            // Avanza indice
            i += gruppo.length - 1;
            continue;
        }
        
        // ESERCIZIO NORMALE
        yPosition = await renderSingleExercise(doc, esercizio, i + 1, margin, yPosition, contentWidth, margin, apiBaseUrl) + 2;
    }
    
    // Note finali rimosse - non servono per scheda cartacea
    
    // ========== FOOTER ==========
    
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text(`Pagina ${i} di ${totalPages}`, pageWidth - margin - 20, pageHeight - 10);
    }
    
    return doc;
};

/**
 * Render singolo esercizio (usato sia per normali che superset)
 */
const renderSingleExercise = async (doc, esercizio, index, startX, startY, width, margin, apiBaseUrl) => {
    let yPos = startY;
    const boxStartY = yPos;
    
    yPos += 3;
    
    const padding = 3; // Padding interno del box
    const columnGap = 3; // Spazio tra le due colonne
    
    // Layout a 2 colonne: sinistra per testo (60%), destra per immagine (40%)
    const leftColWidth = width * 0.6 - padding - (columnGap / 2);
    const rightColWidth = width * 0.4 - padding - (columnGap / 2);
    const leftColX = startX + padding;
    const rightColX = startX + width * 0.6 + (columnGap / 2);
    
    let headerY = yPos;
    
    // ========== COLONNA SINISTRA: Nome + Dettagli ==========
    
    // Nome esercizio
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 64, 175);
    const exerciseName = `${index}. ${esercizio.nome.toUpperCase()}`;
    const nameLines = doc.splitTextToSize(exerciseName, leftColWidth);
    doc.text(nameLines, leftColX, yPos);
    
    // Calcola altezza nome
    const nameHeight = nameLines.length * 3;
    yPos += nameHeight + 1;
    
    // Dettagli esercizio (serie x ripetizioni/secondi/minuti | peso | recupero)
    doc.setFillColor(241, 245, 249);
    const detailsBoxHeight = 5.5;
    doc.roundedRect(leftColX, yPos, leftColWidth, detailsBoxHeight, 1, 1, 'F');
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(51, 65, 85);
    
    // Usa formatExerciseDisplay per unità corretta (ripetizioni/secondi/minuti)
    const repsDisplay = isPyramidExercise(esercizio)
        ? formatSeriesPlanSummary(ensureSeriesPlan(esercizio))
        : (formatWorkoutRepsTarget(esercizio) || formatExerciseDisplay(esercizio.ripetizioni, esercizio));
    const pesoDisplay = formatWorkoutWeightTarget(esercizio);
    const pesoText = pesoDisplay !== '0' ? `${pesoDisplay}kg | ` : '';
    const infoText = isPyramidExercise(esercizio)
        ? `Piramidale: ${repsDisplay} | ${pesoText}${formatRecoveryDisplaySeconds(esercizio.tempo_recupero)}`
        : `${esercizio.serie}x${repsDisplay} | ${pesoText}${formatRecoveryDisplaySeconds(esercizio.tempo_recupero)}`;
    doc.text(infoText, leftColX + 2, yPos + 3.5);

    const typeConfig = getWorkoutSetTypePdfConfig(esercizio.set_type);
    const badgeExtraHeight = typeConfig ? 4.5 : 0;
    const leftColHeight = nameHeight + detailsBoxHeight + 2 + badgeExtraHeight;
    if (typeConfig) {
        drawSetTypePdfBadge(doc, esercizio.set_type, leftColX, yPos + detailsBoxHeight + 0.5);
    }
    
    // ========== COLONNA DESTRA: Immagine ==========
    
    let hasImage = false;
    let imageWidth = 0;
    let imageHeight = 0;
    const maxImageWidth = rightColWidth;
    const maxImageHeight = leftColHeight; // Altezza della colonna sinistra
    
    if (esercizio.immagine_nome) {
        try {
            const imageUrl = `${apiBaseUrl}/serve_image.php?filename=${encodeURIComponent(esercizio.immagine_nome)}`;
            console.log('[PDF Standard] Caricamento immagine_nome:', {
                esercizio: esercizio.nome,
                immagine_nome: esercizio.immagine_nome,
                apiBaseUrl: apiBaseUrl,
                imageUrl: imageUrl
            });
            const img = await loadImageWithDimensions(imageUrl);
            console.log('[PDF Standard] Immagine caricata:', {
                esercizio: esercizio.nome,
                width: img?.width,
                height: img?.height,
                success: !!img
            });
            
            if (img && img.width && img.height) {
                // Calcola dimensioni proporzionate mantenendo aspect ratio
                const aspectRatio = img.width / img.height;
                
                // Prova prima con limite larghezza
                imageWidth = maxImageWidth;
                imageHeight = imageWidth / aspectRatio;
                
                // Se supera limite altezza, usa quello come riferimento
                if (imageHeight > maxImageHeight) {
                    imageHeight = maxImageHeight;
                    imageWidth = imageHeight * aspectRatio;
                }
                
                // Verifica che non esca dalla colonna destra
                if (imageWidth > maxImageWidth) {
                    imageWidth = maxImageWidth;
                    imageHeight = imageWidth / aspectRatio;
                }
                
                // Centra l'immagine verticalmente nella colonna destra
                const imageX = rightColX;
                const imageY = headerY + (leftColHeight - imageHeight) / 2;
                
                doc.addImage(img.dataUrl, 'PNG', imageX, imageY, imageWidth, imageHeight);
                hasImage = true;
            }
        } catch (err) {
            console.warn(`Immagine esercizio ${esercizio.nome} non caricata:`, err);
        }
    }
    
    // Se non c'è immagine, mostra gruppo muscolare nella colonna destra
    if (!hasImage) {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(100, 116, 139);
        const gruppoText = esercizio.gruppo_muscolare || 'N/A';
        const textWidth = doc.getTextWidth(gruppoText);
        doc.text(gruppoText, rightColX + (rightColWidth - textWidth) / 2, headerY + 2);
    }
    
    // yPos ora è alla fine della sezione header (nome + dettagli)
    yPos = headerY + leftColHeight + 1;
    
    // Tabella serie con 6 sessioni
    const serieData = [];
    
    // Header con numeri sessione
    const headerRow = ['', '1', '2', '3', '4', '5', '6'];
    
    // Righe per ogni serie
    for (let s = 1; s <= parseInt(esercizio.serie); s++) {
        serieData.push([
            `S${s}:`,
            '__/__',
            '__/__',
            '__/__',
            '__/__',
            '__/__',
            '__/__'
        ]);
    }
    
    // Tabella serie occupa tutta la larghezza (entrambe le colonne)
    autoTable(doc, {
        startY: yPos,
        head: [headerRow],
        body: serieData,
        margin: { left: startX + padding, right: startX + width - padding },
        tableWidth: width - (padding * 2),
        theme: 'grid',
        headStyles: {
            fontSize: 7,
            fontStyle: 'bold',
            fillColor: [241, 245, 249],
            textColor: [71, 85, 105],
            halign: 'center',
            cellPadding: 1
        },
        styles: {
            fontSize: 7,
            cellPadding: 1,
            textColor: [71, 85, 105],
            lineColor: [203, 213, 225],
            lineWidth: 0.1
        },
        columnStyles: {
            0: { fontStyle: 'bold', cellWidth: 12, halign: 'left' },
            1: { cellWidth: 'auto', halign: 'center' },
            2: { cellWidth: 'auto', halign: 'center' },
            3: { cellWidth: 'auto', halign: 'center' },
            4: { cellWidth: 'auto', halign: 'center' },
            5: { cellWidth: 'auto', halign: 'center' },
            6: { cellWidth: 'auto', halign: 'center' }
        }
    });
    
    yPos = doc.lastAutoTable.finalY + 1;
    
    // Note trainer (se presenti) - ultra compatte (NOTE: e testo sulla stessa riga)
    // Parsa note JSON o stringa legacy per estrarre testo trainer
    const trainerNote = parseExerciseNote(esercizio.note);
    
    if (trainerNote) {
        doc.setFontSize(6);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(146, 64, 14);
        
        // Calcola spazio disponibile per la nota (larghezza totale - padding - spazio per "NOTE: ")
        const noteLabelWidth = doc.getTextWidth('NOTE: ');
        const availableNoteWidth = width - (padding * 2) - noteLabelWidth;
        
        // Dividi il testo della nota in righe se necessario
        const noteLines = doc.splitTextToSize(trainerNote, availableNoteWidth);
        
        // Prima riga: "NOTE: " + testo (sulla stessa riga)
        doc.text('NOTE:', startX + padding, yPos + 1.5);
        doc.setFont('helvetica', 'normal');
        doc.text(noteLines[0], startX + padding + noteLabelWidth, yPos + 1.5);
        
        // Se ci sono altre righe, continua sulla riga successiva
        if (noteLines.length > 1) {
            for (let i = 1; i < noteLines.length; i++) {
                yPos += 2;
                doc.text(noteLines[i], startX + padding + noteLabelWidth, yPos + 1.5);
            }
        }
        
        yPos += 2; // Spazio finale dopo le note
    }
    
    // Box finale
    const boxHeight = yPos - boxStartY;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.roundedRect(startX, boxStartY, width, boxHeight, 3, 3);
    
    return yPos; // Ritorna yPosition finale
};

/**
 * Parsa note esercizio (JSON o stringa legacy) ed estrae testo trainer
 * Supporta backward compatibility con note legacy (stringa semplice)
 */
const parseExerciseNote = (note) => {
    if (!note) return null;
    
    // Se è una stringa che inizia con '{', prova a parsare come JSON
    if (typeof note === 'string' && note.trim().startsWith('{')) {
        try {
            const notesObj = JSON.parse(note);
            // Estrai trainer (priorità) o user/system se trainer non presente
            return notesObj.trainer || notesObj.user || notesObj.system || null;
        } catch (e) {
            // Se non è JSON valido, considera come stringa legacy
            return note.trim();
        }
    }
    
    // Se è già un oggetto (già parsato)
    if (typeof note === 'object' && note !== null) {
        return note.trainer || note.user || note.system || null;
    }
    
    // Note legacy: stringa semplice
    return note.trim() || null;
};

/**
 * Carica logo con dimensioni (supporta PNG, JPG, SVG)
 * Restituisce oggetto con dataUrl, width, height, format
 */
const loadLogoWithDimensions = (url) => {
    return new Promise(async (resolve, reject) => {
        // Verifica se è SVG
        const isSvg = url.toLowerCase().endsWith('.svg') || url.includes('.svg?') || url.includes('type=svg');
        
        if (isSvg) {
            // Gestione SVG: carica come testo e converte in immagine
            try {
                const response = await fetch(url);
                const svgText = await response.text();
                
                // Crea un'immagine da SVG
                const img = new Image();
                const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
                const svgUrl = URL.createObjectURL(svgBlob);
                
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    // Usa dimensioni naturali dell'SVG per mantenere proporzioni
                    canvas.width = img.naturalWidth || img.width || 400;
                    canvas.height = img.naturalHeight || img.height || 400;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    const dataUrl = canvas.toDataURL('image/png');
                    URL.revokeObjectURL(svgUrl);
                    resolve({
                        dataUrl: dataUrl,
                        width: canvas.width,
                        height: canvas.height,
                        format: 'PNG'
                    });
                };
                
                img.onerror = () => {
                    URL.revokeObjectURL(svgUrl);
                    reject(new Error('Errore caricamento SVG'));
                };
                
                img.src = svgUrl;
            } catch (err) {
                reject(err);
            }
        } else {
            // Gestione PNG/JPG normale - usa loadImageWithDimensions
            loadImageWithDimensions(url)
                .then(result => resolve(result))
                .catch(err => reject(err));
        }
    });
};

/**
 * Converti URL immagine in base64 (supporta PNG, JPG, SVG)
 * @deprecated Usare loadLogoWithDimensions per mantenere proporzioni
 */
const urlToBase64 = (url) => {
    return new Promise(async (resolve, reject) => {
        // Verifica se è SVG
        const isSvg = url.toLowerCase().endsWith('.svg') || url.includes('.svg?') || url.includes('type=svg');
        
        if (isSvg) {
            // Gestione SVG: carica come testo e converte in immagine
            try {
                const response = await fetch(url);
                const svgText = await response.text();
                
                // Crea un'immagine da SVG
                const img = new Image();
                const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
                const svgUrl = URL.createObjectURL(svgBlob);
                
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    // Usa dimensioni standard per logo (puoi aumentare per qualità migliore)
                    canvas.width = 400; // 400px per qualità alta quando ridimensionato a 20mm
                    canvas.height = 400;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    const dataUrl = canvas.toDataURL('image/png');
                    URL.revokeObjectURL(svgUrl);
                    resolve(dataUrl);
                };
                
                img.onerror = () => {
                    URL.revokeObjectURL(svgUrl);
                    reject(new Error('Errore caricamento SVG'));
                };
                
                img.src = svgUrl;
            } catch (err) {
                reject(err);
            }
        } else {
            // Gestione PNG/JPG normale
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = reject;
            img.src = url;
        }
    });
};

/**
 * Carica immagine e restituisce dati con dimensioni
 */
const loadImageWithDimensions = (url) => {
    return new Promise((resolve, reject) => {
        console.log(`[PDF Standard - loadImageWithDimensions] Inizio caricamento: "${url}"`);
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
            console.log(`[PDF Standard - loadImageWithDimensions] onload chiamato:`, {
                url: url,
                naturalWidth: img.naturalWidth,
                naturalHeight: img.naturalHeight,
                width: img.width,
                height: img.height
            });
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const dataUrl = canvas.toDataURL('image/png');
            console.log(`[PDF Standard - loadImageWithDimensions] Canvas creato, dataUrl length: ${dataUrl.length}`);
            resolve({
                dataUrl: dataUrl,
                width: img.width,
                height: img.height
            });
        };
        img.onerror = (err) => {
            console.error(`[PDF Standard - loadImageWithDimensions] onerror chiamato:`, {
                url: url,
                error: err
            });
            reject(err);
        };
        console.log(`[PDF Standard - loadImageWithDimensions] Impostazione img.src: "${url}"`);
        img.src = url;
    });
};

